/**
 * The branded hero panel (CAOS-131).
 *
 * This is the shared, reusable "teal side" of the two pre-install surfaces — the branded
 * ground the sign-in mockup and the empty-workspace mockup both carry. It is authored ONCE
 * here as a discrete, prop-driven builder so the sign-in surface (CAOS-132) and the
 * empty-workspace surface (CAOS-133) each EMBED it with their own copy/motif rather than
 * re-implementing the brand chrome. See {@link HeroProps} for the consumer contract.
 *
 * Token discipline (the whole point of the seam):
 *   - The ground is `--caos-gradient-ground`; headline ink is `--caos-color-hero-ink`, body is
 *     `--caos-color-hero-muted` — no hard-coded colour anywhere.
 *   - The wordmark and its OS suffix are NOT literals: they render from `--brand-wordmark` /
 *     `--brand-os-suffix` via CSS `content:` (the tokens are quoted CSS strings baked for
 *     exactly this), so a brand swap ([data-brand=acme]) changes the VISIBLE wordmark text
 *     ("CAOS" → "ACME OS"), not merely the accent. The accent (line-art, the headline's
 *     trailing period, the status dot, the rule) is `--brand-accent`, which the same swap
 *     repaints. One swap, both channels.
 *   - No inline `style=` and no external URL: all styling is in the imported `hero.css`
 *     (`style-src 'self'`); the schematic is inline SVG; the mark is a self-hosted,
 *     bundler-fingerprinted asset (`img-src 'self'`). CSP holds.
 */

import './hero.css';
import markUrl from '../assets/caos-mark.png';
import { heroGlyph, heroMotif, type HeroGlyph, type HeroMotif } from './hero-motif.js';

export type { HeroMotif, HeroGlyph } from './hero-motif.js';

/** One value-proposition tile in the hero's feature grid (the sign-in mockup's four props). */
export interface HeroFeature {
  /** Monoline glyph drawn at the tile's head. */
  readonly icon: HeroGlyph;
  /** Short mono, upper-case label, e.g. "METADATA FIRST". */
  readonly label: string;
  /** One-to-three lines of supporting copy. */
  readonly body: string;
}

/** The props a surface passes to embed the branded hero. All copy is caller-owned; only the */
/** wordmark/OS-suffix come from the brand-token seam so a white-label repaints them. */
export interface HeroProps {
  /**
   * Which line-art schematic sits behind the mark: `radar` (sign-in) or `stack`
   * (empty-workspace). Drives the motif and a layout modifier class.
   */
  readonly motif: HeroMotif;
  /** The display headline. A single trailing "." is styled as the accent period automatically. */
  readonly headline: string;
  /** Supporting copy under the headline — a string, or several strings rendered as lines. */
  readonly body: string | readonly string[];
  /** Optional status chips above the headline (e.g. ["PROVISIONED AND READY","NO APPS INSTALLED"]). */
  readonly eyebrow?: readonly string[];
  /** Optional value-prop grid (the sign-in mockup's four tiles). Omit to render none. */
  readonly features?: readonly HeroFeature[];
  /**
   * Optional action controls slotted beneath the copy (the empty-workspace CTAs). Nodes are
   * appended verbatim, so a surface passes its own `@caos/ui` buttons and keeps ownership of
   * their behaviour (CAOS-133 supplies the boot actions).
   */
  readonly actions?: readonly Node[];
  /**
   * Footer status segments; the first gets the live "online" dot. Defaults to the kernel-online
   * line from the mockups.
   */
  readonly footer?: readonly string[];
  /** Accessible name for the brand lockup. Defaults to the resolved wordmark, else the OS suffix. */
  readonly brandLabel?: string;
}

const DEFAULT_FOOTER = ['ENGINE ONLINE', 'VERSION 1.0.0', 'WHITE-LABEL READY'] as const;

/**
 * Build the branded hero panel element. The surface mounts the returned `<section>` beside its
 * own content (the sign-in card, or nothing extra for the empty-workspace CTAs live inside the
 * hero via {@link HeroProps.actions}).
 */
export function renderHero(props: HeroProps): HTMLElement {
  const section = elx('section', 'caos-hero', `caos-hero--${props.motif}`);
  section.setAttribute('data-motif', props.motif);

  const content = elx('div', 'caos-hero__content');
  content.append(brandLockup(props.brandLabel));
  if (props.eyebrow?.length) content.append(eyebrow(props.eyebrow));
  content.append(headline(props.headline), body(props.body), rule());
  if (props.features?.length) content.append(featureGrid(props.features));
  if (props.actions?.length) content.append(actions(props.actions));
  content.append(footer(props.footer ?? DEFAULT_FOOTER));

  const art = elx('div', 'caos-hero__art');
  art.setAttribute('aria-hidden', 'true');
  art.append(heroMotif(props.motif, markUrl));

  section.append(content, art);
  return section;
}

/** A `<tag>` with class names. */
function elx<K extends keyof HTMLElementTagNameMap>(tag: K, ...classes: string[]): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = classes.join(' ');
  return node;
}

/** The mark + wordmark + OS-suffix lockup. Wordmark/suffix render from the brand tokens. */
function brandLockup(label?: string): HTMLElement {
  const lockup = elx('div', 'caos-hero__lockup');
  lockup.setAttribute('role', 'img');
  lockup.setAttribute('aria-label', label ?? resolveBrandLabel());

  const mark = elx('img', 'caos-hero__mark');
  mark.src = markUrl;
  mark.alt = '';
  mark.setAttribute('aria-hidden', 'true');
  mark.width = 40;
  mark.height = 40;

  const word = elx('div', 'caos-hero__word');
  // Text comes from CSS `content: var(--brand-wordmark)` / `var(--brand-os-suffix)` — see hero.css.
  word.append(elx('span', 'caos-hero__wordmark'), elx('span', 'caos-hero__ossuffix'));

  lockup.append(mark, word);
  return lockup;
}

/** Resolve the brand name for the lockup's a11y label from the token, stripping its quotes. (vocabulary-ok: quotes as in quotation marks) */
function resolveBrandLabel(): string {
  try {
    const root = document.documentElement;
    const w = getComputedStyle(root).getPropertyValue('--brand-wordmark').trim().replace(/^["']|["']$/g, '');
    const s = getComputedStyle(root).getPropertyValue('--brand-os-suffix').trim().replace(/^["']|["']$/g, '');
    const name = [w, s].filter(Boolean).join(' ').trim();
    return name || 'Cloud Atlantis OS';
  } catch {
    return 'Cloud Atlantis OS';
  }
}

/** The status-chip eyebrow row above the headline. */
function eyebrow(chips: readonly string[]): HTMLElement {
  const row = elx('div', 'caos-hero__eyebrow');
  chips.forEach((text, i) => {
    if (i > 0) row.append(elx('span', 'caos-hero__eyebrow-sep'));
    const chip = elx('span', 'caos-hero__chip');
    if (i === 0) chip.classList.add('caos-hero__chip--live');
    chip.textContent = text;
    row.append(chip);
  });
  return row;
}

/** The display headline; a single trailing period is split into an accent span. */
function headline(text: string): HTMLElement {
  const h = elx('h1', 'caos-hero__headline');
  if (text.endsWith('.')) {
    h.append(document.createTextNode(text.slice(0, -1)));
    const dot = elx('span', 'caos-hero__dot');
    dot.textContent = '.';
    h.append(dot);
  } else {
    h.textContent = text;
  }
  return h;
}

/** The muted supporting copy. */
function body(copy: string | readonly string[]): HTMLElement {
  const p = elx('p', 'caos-hero__body');
  const lines = typeof copy === 'string' ? [copy] : copy;
  lines.forEach((line, i) => {
    if (i > 0) p.append(document.createElement('br'));
    p.append(document.createTextNode(line));
  });
  return p;
}

/** The short accent rule under the copy. */
function rule(): HTMLElement {
  const r = elx('div', 'caos-hero__rule');
  r.setAttribute('aria-hidden', 'true');
  return r;
}

/** The value-prop grid. */
function featureGrid(features: readonly HeroFeature[]): HTMLElement {
  const grid = elx('div', 'caos-hero__features');
  for (const f of features) {
    const tile = elx('div', 'caos-hero__feature');

    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('class', 'caos-hero__feature-icon');
    icon.setAttribute('aria-hidden', 'true');
    icon.append(heroGlyph(f.icon));

    const label = elx('div', 'caos-hero__feature-label');
    label.textContent = f.label;

    const text = elx('p', 'caos-hero__feature-body');
    text.textContent = f.body;

    tile.append(icon, label, text);
    grid.append(tile);
  }
  return grid;
}

/** The action row (surface-supplied controls). */
function actions(controls: readonly Node[]): HTMLElement {
  const row = elx('div', 'caos-hero__actions');
  row.append(...controls);
  return row;
}

/** The footer status line; the first segment carries the live dot. */
function footer(segments: readonly string[]): HTMLElement {
  const foot = elx('div', 'caos-hero__footer');
  segments.forEach((text, i) => {
    if (i > 0) foot.append(sep());
    const seg = elx('span', 'caos-hero__foot-seg');
    if (i === 0) {
      const dot = elx('span', 'caos-hero__foot-dot');
      dot.setAttribute('aria-hidden', 'true');
      seg.append(dot);
    }
    seg.append(document.createTextNode(text));
    foot.append(seg);
  });
  return foot;
}

/** A vertical divider between footer segments. */
function sep(): HTMLElement {
  const s = elx('span', 'caos-hero__foot-sep');
  s.setAttribute('aria-hidden', 'true');
  s.textContent = '|';
  return s;
}
